import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Upload, FileText, Send, CheckCircle2, Trash2, Pencil, Clock, Target, FileSignature } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import NombreCliente from '../../components/NombreCliente';
import SelectorCliente from '../../components/SelectorCliente';
import InputMonto from '../../components/InputMonto';
import ModalTrabajo from './ModalTrabajo';
import ModalAccesoPortal from './ModalAccesoPortal';
import { money, moneyK, fecha, Title, Badge, Empty, Kpi } from './ui';

const ESTADO = {
  borrador: { label: 'Borrador', color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
  a_preparar: { label: 'A preparar', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  a_ofrecer: { label: 'A ofrecer', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  enviada: { label: 'Enviada', color: '#a855f7', bg: 'rgba(168,85,247,0.15)' },
  vista: { label: 'En seguimiento', color: '#0ea5e9', bg: 'rgba(14,165,233,0.15)' },
  confirmada: { label: 'Cerrada', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  no_cerro: { label: 'No cerró', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
};

export default function AgenciaPropuestas() {
  const [rows, setRows] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [edit, setEdit] = useState(null);     // alta / edición
  const [cerrar, setCerrar] = useState(null); // armado del trabajo
  const [acceso, setAcceso] = useState(null);  // alta del portal al confirmar
  const navigate = useNavigate();

  const cargar = () => api.get('/agency/propuestas').then(r => setRows(r.data)).catch(() => toast.error('No se pudo cargar'));
  useEffect(() => {
    cargar();
    api.get('/agency/clientes').then(r => setClientes(r.data)).catch(() => {});
  }, []);

  const guardar = async (e) => {
    e.preventDefault();
    const p = edit;
    try {
      const cuerpo = {
        titulo: p.titulo, cliente_id: p.cliente_id || '', cliente_nuevo: p.cliente_nuevo || '',
        monto: p.monto || null, vigencia_dias: p.vigencia_dias || 30, estado: p.estado || 'borrador',
        datos: { email_contacto: p.email_contacto || '', notas: p.notas || '' },
      };
      const id = p.id || (await api.post('/agency/propuestas', cuerpo)).data.id;
      if (p.id) await api.put('/agency/propuestas/' + p.id, cuerpo);
      if (p.archivo) {
        const fd = new FormData(); fd.append('pdf', p.archivo);
        await api.post(`/agency/propuestas/${id}/pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Guardada');
      setEdit(null); cargar();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo guardar'); }
  };

  const cambiarEstado = async (p, estado) => {
    if (estado === p.estado) return;
    if (estado === 'confirmada') return abrirCierre(p);
    try { await api.put('/agency/propuestas/' + p.id, { estado }); cargar(); } catch { toast.error('Error'); }
  };

  const enviar = async (p) => {
    try {
      const { data } = await api.post(`/agency/propuestas/${p.id}/enviar`);
      toast.success('Enviada a ' + data.destinos.join(', '), { duration: 6000 });
      cargar();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo enviar'); }
  };

  const borrar = async (p) => {
    if (!window.confirm(`¿Borrar la propuesta "${p.titulo}"?`)) return;
    try { await api.delete('/agency/propuestas/' + p.id); cargar(); } catch { toast.error('Error'); }
  };

  const abrirCierre = (p) => {
    if (!p.cliente_id) return toast.error('Primero asignale un cliente a la propuesta');
    setCerrar(p);
  };

  if (!rows) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '8px' };
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const abiertas = rows.filter(r => !['confirmada', 'no_cerro'].includes(r.estado));
  const ganadas = rows.filter(r => r.estado === 'confirmada');
  const cerradas = rows.filter(r => ['confirmada', 'no_cerro'].includes(r.estado));

  return (
    <div>
      <Title right={<button className="btn btn-primary" onClick={() => setEdit({ titulo: '', cliente_id: '', vigencia_dias: 30, estado: 'borrador' })}><Plus size={16} /> Nueva propuesta</button>}>
        Propuestas
      </Title>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi icono={Clock} label="En seguimiento" value={abiertas.length} sub={moneyK(abiertas.reduce((a, r) => a + Number(r.monto || 0), 0))} color="#0ea5e9" />
        <Kpi icono={CheckCircle2} label="Cerradas" value={ganadas.length} sub={moneyK(ganadas.reduce((a, r) => a + Number(r.monto || 0), 0))} color="#22c55e" />
        <Kpi icono={Target} label="Efectividad" value={cerradas.length ? Math.round((ganadas.length / cerradas.length) * 100) + '%' : '—'} sub={`${cerradas.length} decididas`} />
        <Kpi icono={FileSignature} label="Total" value={rows.length} />
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-container">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Propuesta</th><th style={th}>Cliente</th><th style={{ ...th, textAlign: 'right' }}>Monto</th>
              <th style={th}>Estado</th><th style={th}>Creada</th><th style={th}>Enviada</th><th style={th}>PDF</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={8} style={td}><Empty>Todavía no cargaste ninguna propuesta</Empty></td></tr>
                : rows.map(p => {
                  const e = ESTADO[p.estado] || {};
                  return (
                    <tr key={p.id}>
                      <td style={{ ...td, fontWeight: 600 }}>{p.titulo}
                        {p.datos?.notas && <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>{p.datos.notas}</div>}</td>
                      <td style={td}>{p.cliente
                        ? <NombreCliente nombre={p.cliente} logo={p.logo_url} style={{ cursor: 'pointer' }}
                            onClick={() => navigate('/agencia/clientes/' + p.cliente_id)} />
                        : <span style={{ color: 'var(--text-muted)' }}>Sin cliente</span>}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{p.monto ? money(p.monto) : '—'}</td>
                      <td style={td}>
                        <select value={p.estado} onChange={ev => cambiarEstado(p, ev.target.value)} title="Cambiar estado"
                          style={{ width: 'auto', padding: '4px 26px 4px 10px', fontSize: 12, fontWeight: 700, borderRadius: 999,
                            background: e.bg || 'transparent', color: e.color, border: '1px solid var(--border)' }}>
                          {Object.entries(ESTADO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: 12 }}>{p.created_at ? fecha(p.created_at) : '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: 12 }}>{p.enviada_at ? fecha(p.enviada_at) : '—'}</td>
                      <td style={td}>{p.pdf_url
                        ? <a href={p.pdf_url} target="_blank" rel="noopener noreferrer" title="Ver el PDF"><FileText size={16} /></a>
                        : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEdit({ ...p, email_contacto: p.datos?.email_contacto || '', notas: p.datos?.notas || '' })} title="Editar"><Pencil size={14} /></button>
                        {p.pdf_url && <button className="btn btn-ghost btn-sm" onClick={() => enviar(p)} title="Enviar por mail con el PDF adjunto"><Send size={14} /></button>}
                        {p.estado !== 'confirmada' && <button className="btn btn-ghost btn-sm" onClick={() => abrirCierre(p)} title="Se cerró: armar el trabajo"><CheckCircle2 size={14} color="#22c55e" /></button>}
                        <button className="btn btn-ghost btn-sm" onClick={() => borrar(p)} title="Borrar"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alta y edición */}
      {edit && (
        <div className="modal-overlay" onClick={() => setEdit(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <h2>{edit.id ? 'Editar propuesta' : 'Nueva propuesta'}</h2>
            <form onSubmit={guardar}>
              <div className="form-group"><label>Título</label>
                <input value={edit.titulo} onChange={e => setEdit({ ...edit, titulo: e.target.value })} placeholder="Sitio web + hosting anual" required autoFocus /></div>

              <div className="form-group"><label>Cliente</label>
                <SelectorCliente clientes={clientes} clienteId={edit.cliente_id} clienteNuevo={edit.cliente_nuevo}
                  onCambio={v => setEdit({ ...edit, ...v })} />
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6 }}>
                  Podés dejarla sin cliente y asignárselo más adelante desde el lápiz.
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Monto</label>
                  <InputMonto value={edit.monto ?? ''} onChange={v => setEdit({ ...edit, monto: v })} /></div>
                <div className="form-group"><label>Vigencia (días)</label>
                  <input type="number" value={edit.vigencia_dias || 30} onChange={e => setEdit({ ...edit, vigencia_dias: e.target.value })} /></div>
              </div>

              <div className="form-group"><label>Email para enviarla</label>
                <input type="email" value={edit.email_contacto || ''} onChange={e => setEdit({ ...edit, email_contacto: e.target.value })} placeholder="Si es distinto al del cliente" /></div>

              <div className="form-group"><label>Notas internas</label>
                <input value={edit.notas || ''} onChange={e => setEdit({ ...edit, notas: e.target.value })} placeholder="Cómo viene la charla, cuándo hacer seguimiento" /></div>

              <div className="form-group"><label>PDF de la propuesta</label>
                <input type="file" accept="application/pdf" onChange={e => setEdit({ ...edit, archivo: e.target.files[0] })} />
                {edit.pdf_url && !edit.archivo && (
                  <div style={{ fontSize: 12, marginTop: 6 }}><Upload size={12} /> Ya tiene uno cargado · <a href={edit.pdf_url} target="_blank" rel="noopener noreferrer">verlo</a></div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setEdit(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {acceso && (
        <ModalAccesoPortal cliente_id={acceso.id} clienteNombre={acceso.nombre}
          onClose={() => { const c = acceso.id; setAcceso(null); navigate('/agencia/clientes/' + c); }} />
      )}

      {cerrar && (
        <ModalTrabajo
          cliente_id={cerrar.cliente_id} clienteNombre={cerrar.cliente}
          nombreInicial={cerrar.titulo} montoInicial={cerrar.monto || ''} propuestaId={cerrar.id}
          onClose={() => setCerrar(null)}
          onListo={() => { setAcceso({ id: cerrar.cliente_id, nombre: cerrar.cliente }); setCerrar(null); cargar(); }}
        />
      )}
    </div>
  );
}
